import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useDiagnostic } from '@/contexts/DiagnosticContext';
import { Button } from '@/domains/shared/components/Button';

const STAGES = [
  'Validando token de segurança e integridade...',
  'Calculando notas das 6 dimensões estratégicas...',
  'Aplicando regras de prioridade e matriz de desempate...',
  'Construindo seu radar de presença e devolutiva...',
];

export function ProcessingPage() {
  const { navigate } = useRouter();
  const { processSubmission } = useDiagnostic();
  const [stage, setStage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef<boolean>(false);

  const executeSubmission = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;

    setError(null);
    setStage(0);

    const interval = setInterval(() => {
      setStage((prev) => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 350);

    try {
      const res = await processSubmission();
      clearInterval(interval);
      navigate(`/resultado?token=${res.publicToken}`);
    } catch (err) {
      clearInterval(interval);
      submittingRef.current = false;
      setError(
        err instanceof Error
          ? err.message
          : 'Ocorreu uma instabilidade ao processar seu diagnóstico. Por favor, tente novamente.',
      );
    }
  }, [navigate, processSubmission]);

  useEffect(() => {
    executeSubmission();
  }, [executeSubmission]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-navy px-4 text-center text-brand-offWhite">
        <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-navy-dark p-8 shadow-2xl">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 text-red-400">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="mt-4 text-xl font-bold text-white sm:text-2xl">
            Não foi possível concluir seu diagnóstico
          </h1>

          <p className="mt-3 text-sm text-brand-offWhite/80 leading-relaxed">
            {error}
          </p>

          <div className="mt-6 flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={() => navigate('/consentimento')}
              className="w-full"
            >
              Refazer verificação de segurança
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/identificacao')}
              className="w-full"
            >
              Revisar Dados de Contato
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-navy px-4 text-center text-brand-offWhite">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-dark p-8 shadow-2xl">
        {/* Spinner elegante com as cores da marca */}
        <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
          <div className="absolute h-full w-full animate-spin rounded-full border-4 border-white/10 border-t-brand-orange" />
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-orange text-sm font-black text-white shadow-md">
            IC
          </div>
        </div>

        <h1 className="mt-6 text-xl font-bold text-white sm:text-2xl">
          Processando seu diagnóstico
        </h1>

        <p className="mt-4 text-sm font-medium text-brand-orange animate-pulse" role="status">
          {STAGES[stage]}
        </p>

        <p className="mt-4 text-xs text-brand-offWhite/60">
          Apenas alguns instantes. Estamos gerando sua devolutiva de acordo com as regras de maturidade do playbook.
        </p>
      </div>
    </div>
  );
}
